import React, { useState } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { AlertTriangle, Book, Save, X } from 'lucide-react';
import { Block, TradingPlatform, AssetClass } from '../../types';
import { createTemplate } from '../../services/templateService';
import { useAuth } from '../../contexts/AuthContext';

interface CreateTemplateDialogProps {
  blocks: Block[];
  onClose: () => void;
  onCreated: (templateId: string) => void;
}

// Define the category type to match your template service expectations
type StrategyCategory =
  | 'trend'
  | 'momentum'
  | 'breakout'
  | 'mean_reversion'
  | 'pattern'
  | 'arbitrage'
  | 'scalping'
  | 'other';
type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';

const CreateTemplateDialog: React.FC<CreateTemplateDialogProps> = ({
  blocks,
  onClose,
  onCreated,
}) => {
  useEscapeKey(onClose);
  const { user } = useAuth();
  const [metadata, setMetadata] = useState({
    name: '',
    description: '',
    platform: TradingPlatform.MT5,
    assetClass: AssetClass.FOREX,
    version: '1.0.0',
    tags: '',
    category: 'trend' as StrategyCategory,
    difficulty: 'intermediate' as DifficultyLevel,
    timeframes: '',
    marketConditions: '',
    isPublic: false,
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!metadata.name.trim()) {
      setError('Template name is required');
      return;
    }

    if (!user) {
      setError('You must be logged in to create a template');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      console.log('Creating template with blocks:', blocks);
      console.log('Metadata:', metadata);

      const templateId = await createTemplate({
        name: metadata.name,
        description: metadata.description,
        blocks,
        platform: metadata.platform,
        assetClass: metadata.assetClass,
        version: metadata.version,
        tags: metadata.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        category: metadata.category,
        difficulty: metadata.difficulty,
        timeframes: metadata.timeframes
          .split(',')
          .map((tf) => tf.trim())
          .filter(Boolean),
        marketConditions: metadata.marketConditions
          .split(',')
          .map((mc) => mc.trim())
          .filter(Boolean),
        isPublic: metadata.isPublic,
        requiredData: [], // Fixed: Changed from {} to [] (empty array)
        defaultParameters: {}, // Keep as object if this is the correct type
      });

      onCreated(templateId);
      onClose();
    } catch (err) {
      console.error('Template creation error:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to create template'
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center">
            <Book className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-800">
              Create Strategy Template
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name
              </label>
              <input
                type="text"
                value={metadata.name}
                onChange={(e) =>
                  setMetadata({ ...metadata, name: e.target.value })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="My Trading Strategy Template"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={metadata.description}
                onChange={(e) =>
                  setMetadata({ ...metadata, description: e.target.value })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Describe your strategy template..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Platform
              </label>
              <select
                value={metadata.platform}
                onChange={(e) =>
                  setMetadata({
                    ...metadata,
                    platform: e.target.value as TradingPlatform,
                  })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.values(TradingPlatform).map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asset Class
              </label>
              <select
                value={metadata.assetClass}
                onChange={(e) =>
                  setMetadata({
                    ...metadata,
                    assetClass: e.target.value as AssetClass,
                  })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.values(AssetClass).map((assetClass) => (
                  <option key={assetClass} value={assetClass}>
                    {assetClass}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={metadata.category}
                onChange={(e) =>
                  setMetadata({
                    ...metadata,
                    category: e.target.value as StrategyCategory,
                  })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="trend">Trend Following</option>
                <option value="momentum">Momentum</option>
                <option value="breakout">Breakout</option>
                <option value="mean_reversion">Mean Reversion</option>
                <option value="pattern">Pattern Trading</option>
                <option value="arbitrage">Arbitrage</option>
                <option value="scalping">Scalping</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Difficulty
              </label>
              <select
                value={metadata.difficulty}
                onChange={(e) =>
                  setMetadata({
                    ...metadata,
                    difficulty: e.target.value as DifficultyLevel,
                  })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Version
              </label>
              <input
                type="text"
                value={metadata.version}
                onChange={(e) =>
                  setMetadata({ ...metadata, version: e.target.value })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1.0.0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <input
                type="text"
                value={metadata.tags}
                onChange={(e) =>
                  setMetadata({ ...metadata, tags: e.target.value })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="trend-following, forex, scalping"
              />
              <p className="mt-1 text-xs text-gray-500">
                Separate tags with commas
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timeframes
              </label>
              <input
                type="text"
                value={metadata.timeframes}
                onChange={(e) =>
                  setMetadata({ ...metadata, timeframes: e.target.value })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="M5, M15, H1"
              />
              <p className="mt-1 text-xs text-gray-500">
                Separate timeframes with commas
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Market Conditions
              </label>
              <input
                type="text"
                value={metadata.marketConditions}
                onChange={(e) =>
                  setMetadata({ ...metadata, marketConditions: e.target.value })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="trending, volatile, ranging"
              />
              <p className="mt-1 text-xs text-gray-500">
                Separate conditions with commas
              </p>
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={metadata.isPublic}
                  onChange={(e) =>
                    setMetadata({ ...metadata, isPublic: e.target.checked })
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label
                  htmlFor="isPublic"
                  className="ml-2 text-sm text-gray-700"
                >
                  Make this template public
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Public templates can be viewed and used by other traders
              </p>
            </div>
          </div>

          {error && (
            <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
              <AlertTriangle className="w-5 h-5 mr-2" />
              {error}
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating}
              className={`flex items-center px-4 py-2 rounded-md text-white font-medium transition-colors ${
                creating
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {creating ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1.5" />
                  Create Template
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateDialog;
